import { createStore, get, set, del } from 'idb-keyval'
import type { ChatChoice, Pending } from '@edge/edge-ai'
import { getIdbUserScope } from '@/workspace/idb-cache'

/**
 * Thread-based companion conversation storage (2026-08-28, replaces the
 * one-conversation-per-route-context model). Every "New chat" mints a thread
 * with its own uuid; the route context survives only as metadata
 * (`page` / `originContextKey` — where the thread STARTED). IndexedDB is the
 * local cache; the sync layer (features/companion/thread-sync.ts) pushes
 * dirty threads to the backend and pulls remote ones.
 */

export interface StoredThreadMessage {
  role: 'user' | 'assistant'
  text: string
  timestamp: number
  toolCalls?: unknown[]
  toolResults?: unknown[]
  /** The buttons an answer that asks "which one?" shows, kept so a reopened thread still has them. */
  choices?: ChatChoice[]
  /** What the answer left the thread waiting on (spec 2026-10-18, 6.2); the last message's is the thread's. */
  pending?: Pending
}

export interface StoredThread {
  id: string
  title: string
  messages: StoredThreadMessage[]
  /** Page the thread STARTED on (metadata for the history list). */
  page: string
  /** Route context key the thread started under (metadata, not the storage key). */
  originContextKey: string
  createdAt: number
  updatedAt: number
  // ── Sync bookkeeping ──
  /** Local change not yet pushed to the server. */
  dirty: boolean
  syncedAt?: number
  remoteUpdatedAt?: number
}

export interface ThreadIndexEntry {
  id: string
  title: string
  page: string
  updatedAt: number
  messageCount: number
  dirty: boolean
}

// ── Legacy (pre-thread) shapes, kept for the one-shot migration ────────────

interface LegacyConversation {
  contextKey: string
  title: string
  messages: StoredThreadMessage[]
  page: string
  createdAt: number
  updatedAt: number
}

interface LegacyIndexEntry {
  contextKey: string
  title: string
  page: string
  updatedAt: number
  messageCount: number
}

// Separate IDB database for conversations
const store = createStore('edge-conversations', 'history')

const MAX_STORED = 50

function threadKey(id: string): string {
  return `${getIdbUserScope()}:thread:${id}`
}

function threadIndexKey(): string {
  return `${getIdbUserScope()}:__threads__`
}

function migratedMarkerKey(): string {
  return `${getIdbUserScope()}:__threads_migrated__`
}

function legacyKey(contextKey: string): string {
  return `${getIdbUserScope()}:${contextKey}`
}

function legacyIndexKey(): string {
  return `${getIdbUserScope()}:__index__`
}

// ── One-shot migration: wrap legacy per-context conversations into threads ──
// Idempotent: re-running after a mid-migration crash dedupes on
// originContextKey (old keys are only deleted after the new index + marker
// are written). Server sync starts from the migrated `dirty: true` threads.

let migrationPromise: Promise<void> | null = null

async function migrateLegacyConversations(): Promise<void> {
  try {
    if (await get<boolean>(migratedMarkerKey(), store)) return
    const legacyIndex = (await get<LegacyIndexEntry[]>(legacyIndexKey(), store)) ?? []
    const index = (await get<ThreadIndexEntry[]>(threadIndexKey(), store)) ?? []
    const migratedContexts = new Set<string>()
    for (const entry of index) {
      const t = await get<StoredThread>(threadKey(entry.id), store)
      if (t) migratedContexts.add(t.originContextKey)
    }

    for (const legacy of legacyIndex) {
      if (migratedContexts.has(legacy.contextKey)) continue
      try {
        const conv = await get<LegacyConversation>(legacyKey(legacy.contextKey), store)
        if (!conv || conv.messages.length === 0) continue
        const thread: StoredThread = {
          id: crypto.randomUUID(),
          title: conv.title || 'Untitled',
          messages: conv.messages,
          page: conv.page,
          originContextKey: legacy.contextKey,
          createdAt: conv.createdAt ?? conv.updatedAt ?? Date.now(),
          updatedAt: conv.updatedAt ?? Date.now(),
          dirty: true, // first sync uploads the migrated history
        }
        await set(threadKey(thread.id), thread, store)
        index.push({
          id: thread.id,
          title: thread.title,
          page: thread.page,
          updatedAt: thread.updatedAt,
          messageCount: thread.messages.length,
          dirty: true,
        })
      } catch {
        /* skip corrupt legacy entries */
      }
    }

    index.sort((a, b) => b.updatedAt - a.updatedAt)
    await set(threadIndexKey(), index, store)
    await set(migratedMarkerKey(), true, store)

    // Old keys go last — a crash before this point just re-runs the (deduped)
    // migration on next load.
    for (const legacy of legacyIndex) {
      await del(legacyKey(legacy.contextKey), store)
    }
    await del(legacyIndexKey(), store)
  } catch {
    /* storage unavailable — threads simply start empty */
  }
}

function ensureMigrated(): Promise<void> {
  migrationPromise ??= migrateLegacyConversations()
  return migrationPromise
}

/** Test seam: reset the memoized migration (per-test IDB resets). */
export function __resetMigrationForTests(): void {
  migrationPromise = null
}

// ── Public API ──────────────────────────────────────────────

export async function getThread(id: string): Promise<StoredThread | null> {
  try {
    await ensureMigrated()
    const raw = await get<StoredThread>(threadKey(id), store)
    return raw ?? null
  } catch {
    return null
  }
}

export async function saveThread(thread: StoredThread): Promise<void> {
  await ensureMigrated()
  await set(threadKey(thread.id), thread, store)

  const index = await getThreadIndex()
  const entry: ThreadIndexEntry = {
    id: thread.id,
    title: thread.title,
    page: thread.page,
    updatedAt: thread.updatedAt,
    messageCount: thread.messages.length,
    dirty: thread.dirty,
  }
  const existing = index.findIndex((e) => e.id === thread.id)
  if (existing >= 0) {
    index[existing] = entry
  } else {
    index.push(entry)
  }

  index.sort((a, b) => b.updatedAt - a.updatedAt)

  // Evict old threads beyond max
  const evicted = index.splice(MAX_STORED)
  for (const e of evicted) {
    await del(threadKey(e.id), store)
  }

  await set(threadIndexKey(), index, store)
}

export async function deleteThread(id: string): Promise<void> {
  await ensureMigrated()
  await del(threadKey(id), store)

  const index = await getThreadIndex()
  await set(
    threadIndexKey(),
    index.filter((e) => e.id !== id),
    store,
  )
}

export async function getThreadIndex(): Promise<ThreadIndexEntry[]> {
  try {
    await ensureMigrated()
    const raw = await get<ThreadIndexEntry[]>(threadIndexKey(), store)
    return raw ?? []
  } catch {
    return []
  }
}
